import { useState } from 'react'
import { Box, CircularProgress } from '@mui/material'
import { L, Icon } from '../ui/LavDropdown'
import { LavSectionTitle } from '../add-appointment/appointmentLavenderFields'
import { MbIc, payMethodIcon, MobileSheet, SheetCancelButton, MbPrimaryButton } from './mbBits'

const OK_BG = '#E9F6EE'
const OK_BORDER = '#C9E8D4'
const OK_IC = '#2F8A5B'

/* ── 34px round stepper (design ck-step, touch-sized) ── */
function Stepper({ value, onBump, disabled }) {
	return (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 'none' }}>
			<Box
				component="button"
				type="button"
				aria-label="Remove one"
				disabled={!value || disabled}
				onClick={() => onBump(-1)}
				sx={{
					width: 34, height: 34, borderRadius: '50%', p: 0, cursor: 'pointer',
					display: 'flex', alignItems: 'center', justifyContent: 'center',
					border: `1.5px solid ${L.border}`, bgcolor: L.field, color: L.muted,
					'&:disabled': { opacity: 0.35, cursor: 'default' },
				}}
			>
				<MbIc name="minus" size={13} />
			</Box>
			<Box component="span" sx={{
				minWidth: 18, textAlign: 'center', fontWeight: 700, fontSize: '15px',
				fontVariantNumeric: 'tabular-nums', color: L.text,
			}}>
				{value}
			</Box>
			<Box
				component="button"
				type="button"
				aria-label="Add one"
				disabled={disabled}
				onClick={() => onBump(1)}
				sx={{
					width: 34, height: 34, borderRadius: '50%', p: 0, border: 'none', cursor: 'pointer',
					display: 'flex', alignItems: 'center', justifyContent: 'center',
					bgcolor: L.primary, color: '#FFFFFF',
					'&:active': { bgcolor: L.primaryDeep },
					'&:disabled': { opacity: 0.5, cursor: 'default' },
				}}
			>
				<MbIc name="plus" size={13} />
			</Box>
		</Box>
	)
}

/* ── add-on row (design ck-row) ── */
function AddonRow({ addon, count, onBump, formatCurrency, disabled }) {
	return (
		<Box sx={{
			bgcolor: count > 0 ? L.primarySoft : L.surface,
			border: `1.5px solid ${count > 0 ? L.primary : 'transparent'}`,
			borderRadius: '13px', p: '11px 13px', flex: 'none',
			display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px',
		}}>
			<Box sx={{ minWidth: 0 }}>
				<Box component="span" sx={{ display: 'block', fontWeight: 600, fontSize: '14.5px', color: L.text }}>
					{addon.name}
				</Box>
				<Box component="span" sx={{ display: 'block', fontSize: '12.5px', color: L.muted, mt: '2px' }}>
					{formatCurrency(addon.price)} each
				</Box>
			</Box>
			<Stepper value={count} onBump={onBump} disabled={disabled} />
		</Box>
	)
}

/* ── tip chip (design ck-chip, 52px) ── */
function TipChip({ on, onClick, top, bottom, disabled }) {
	return (
		<Box
			component="button"
			type="button"
			onClick={onClick}
			disabled={disabled}
			sx={{
				flex: '1 1 0', minWidth: 0, border: `1.5px solid ${on ? L.primary : L.border}`,
				bgcolor: on ? L.primarySoft : L.field, borderRadius: '13px', p: '8px 4px',
				minHeight: 52, display: 'flex', flexDirection: 'column', alignItems: 'center',
				justifyContent: 'center', gap: '2px', cursor: 'pointer', fontFamily: 'inherit',
				'&:active': { borderColor: L.primary },
				'&:disabled': { opacity: 0.5, cursor: 'default' },
			}}
		>
			<Box component="span" sx={{ fontWeight: 700, fontSize: '14px', color: on ? L.primaryDeep : L.text, whiteSpace: 'nowrap' }}>
				{top}
			</Box>
			{bottom && (
				<Box component="span" sx={{ fontSize: '11.5px', color: on ? L.primary : L.muted, whiteSpace: 'nowrap' }}>
					{bottom}
				</Box>
			)}
		</Box>
	)
}

/* ── summary rows (design SummaryRows) ── */
function SummaryRows({ ck, dense = false }) {
	const row = { display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: dense ? '13px' : '14.5px', fontVariantNumeric: 'tabular-nums' }
	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: dense ? '7px' : '9px' }}>
			<Box sx={row}>
				<Box component="span" sx={{ color: L.muted }}>Subtotal</Box>
				<span>{ck.formatCurrency(ck.subtotal)}</span>
			</Box>
			{ck.discountAmountCents > 0 && (
				<Box sx={row}>
					<Box component="span" sx={{ color: L.primaryDeep, fontWeight: 650 }}>{ck.discountLabel}</Box>
					<Box component="span" sx={{ color: L.primaryDeep, fontWeight: 650 }}>−{ck.formatCurrency(ck.discountAmountCents)}</Box>
				</Box>
			)}
			<Box sx={row}>
				<Box component="span" sx={{ color: L.muted }}>Tax ({ck.taxRate}%)</Box>
				<span>{ck.formatCurrency(ck.taxAmount)}</span>
			</Box>
			{ck.tipsAmountCents > 0 && (
				<Box sx={row}>
					<Box component="span" sx={{ color: L.muted }}>Tip{['15', '18', '25'].includes(ck.selectedTipOption) ? ` (${ck.selectedTipOption}%)` : ''}</Box>
					<span>{ck.formatCurrency(ck.tipsAmountCents)}</span>
				</Box>
			)}
			<Box sx={{
				...row, borderTop: `1.5px solid ${L.border}`, pt: dense ? '10px' : '12px', mt: '3px',
				fontWeight: 800, fontSize: dense ? '15.5px' : '17px',
			}}>
				<span>Total</span>
				<span>{ck.formatCurrency(ck.totalAmount)}</span>
			</Box>
		</Box>
	)
}

/* ── receipt lines (service lines + picked add-ons) ── */
function ReceiptLines({ ck }) {
	const picked = ck.addOns.filter(a => (ck.addOnCounts[a.id] || 0) > 0)
	return (
		<>
			{ck.serviceLines.map(l => (
				<Box key={l.key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', fontSize: '14px', fontVariantNumeric: 'tabular-nums' }}>
					<Box component="span" sx={{ minWidth: 0, fontWeight: 650 }}>
						{l.name}
						{l.pet && (
							<Box component="em" sx={{ display: 'block', fontStyle: 'normal', fontSize: '12px', fontWeight: 600, color: L.muted, mt: '1px' }}>
								{l.pet}
							</Box>
						)}
					</Box>
					<Box component="span" sx={{ flex: 'none', fontWeight: 650 }}>{ck.formatCurrency(l.price)}</Box>
				</Box>
			))}
			{picked.map(a => (
				<Box key={a.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', fontSize: '14px', fontVariantNumeric: 'tabular-nums' }}>
					<Box component="span" sx={{ minWidth: 0, fontWeight: 650 }}>
						{a.name}{ck.addOnCounts[a.id] > 1 ? ` ×${ck.addOnCounts[a.id]}` : ''}
					</Box>
					<Box component="span" sx={{ flex: 'none', fontWeight: 650 }}>{ck.formatCurrency(a.price * ck.addOnCounts[a.id])}</Box>
				</Box>
			))}
			{(ck.legacyAddOns || []).map(l => (
				<Box key={l.key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', fontSize: '14px', fontVariantNumeric: 'tabular-nums' }}>
					<Box component="span" sx={{ minWidth: 0, fontWeight: 650 }}>{l.name}</Box>
					<Box component="span" sx={{ flex: 'none', fontWeight: 650 }}>{ck.formatCurrency(l.price)}</Box>
				</Box>
			))}
			<Box sx={{ borderTop: `1.5px dashed ${L.border}`, m: '4px 0' }} />
		</>
	)
}

/**
 * MobileCheckout — full-screen mobile checkout per the Claude Design
 * "Mobile · checkout step" artboards: per-pet service cards, an add-ons bottom
 * sheet, a payment-method bottom sheet, tip chips, a sticky total bar that
 * expands into a receipt sheet, and the paid success view. Purely
 * presentational — all state and mutations live in LogPaymentDialog and come
 * in through the `ck` bag.
 */
export default function MobileCheckout({ ck }) {
	const [addonSheet, setAddonSheet] = useState(false)
	const [paySheet, setPaySheet] = useState(false)
	const [receiptSheet, setReceiptSheet] = useState(false)

	const payMethod = ck.availablePaymentMethods.find(m => m.value === ck.paymentMethod)
		|| ck.availablePaymentMethods[0]
		|| { value: 'cash', label: 'Cash' }

	const filteredAddons = ck.addOns.filter(a => a.name.toLowerCase().includes(ck.addOnSearch.toLowerCase()))
	const pickedAddons = ck.addOns.filter(a => (ck.addOnCounts[a.id] || 0) > 0)
	const legacyAddons = ck.legacyAddOns || []

	const bump = (id, delta) => ck.setAddOnCounts(prev => ({
		...prev,
		[id]: Math.max(0, (prev[id] || 0) + delta),
	}))

	const completeLabel = ck.isSquareTerminalMethod ? 'Charge Terminal' : 'Complete Payment'
	const handleComplete = () => {
		setReceiptSheet(false)
		if (ck.isSquareTerminalMethod) ck.onChargeTerminal()
		else if (ck.isSquareCardMethod) ck.onOpenSquareCard()
		else ck.onComplete()
	}

	return (
		<Box sx={{
			height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0,
			bgcolor: L.bg, color: L.text, fontFamily: L.font, fontSize: '14px', position: 'relative',
		}}>
			{/* ── header ── */}
			<Box sx={{ flex: 'none', p: 'calc(env(safe-area-inset-top, 0px) + 16px) 16px 12px' }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
					<Box
						component="button"
						type="button"
						aria-label="Back to appointment details"
						onClick={ck.onBack}
						disabled={ck.isFormLocked && !ck.paidInfo}
						sx={{
							width: 40, height: 40, flex: 'none', ml: '-6px', border: 'none', borderRadius: '12px',
							bgcolor: 'transparent', color: L.text, display: 'flex', alignItems: 'center',
							justifyContent: 'center', cursor: 'pointer',
							'&:active': { bgcolor: L.surface },
							'&:disabled': { opacity: 0.4, cursor: 'default' },
						}}
					>
						<MbIc name="back" size={18} />
					</Box>
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Box component="h1" sx={{
							m: 0, fontSize: '23px', fontWeight: 800, letterSpacing: '-0.02em',
							whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
						}}>
							{ck.paidInfo ? 'Paid' : 'Log Payment'}
						</Box>
						<Box component="p" sx={{
							m: '3px 0 0', fontSize: '13px', fontWeight: 600, color: L.muted,
							whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
						}}>
							{ck.heroSub}
						</Box>
					</Box>
				</Box>
			</Box>

			{ck.paidInfo ? (
				/* ── success view ── */
				<>
					<Box sx={{
						flex: 1, minHeight: 0, overflowY: 'auto', p: '26px 16px 24px',
						display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
					}}>
						<Box sx={{
							width: 62, height: 62, flex: 'none', borderRadius: '20px', bgcolor: OK_BG,
							border: `1.5px solid ${OK_BORDER}`, color: OK_IC,
							display: 'flex', alignItems: 'center', justifyContent: 'center',
						}}>
							<MbIc name="checkc" size={30} sw={1.6} />
						</Box>
						<Box component="h2" sx={{ m: '16px 0 0', fontSize: '21px', fontWeight: 800, letterSpacing: '-0.01em' }}>
							Payment complete
						</Box>
						<Box sx={{
							mt: '6px', fontSize: '34px', fontWeight: 800, letterSpacing: '-0.02em',
							fontVariantNumeric: 'tabular-nums', color: L.primaryDeep,
						}}>
							{ck.formatCurrency(ck.paidInfo.amount)}
						</Box>
						<Box component="p" sx={{ m: '6px 0 0', fontSize: '13.5px', fontWeight: 600, color: L.muted }}>
							{ck.paidInfo.method} · today, {ck.paidInfo.when}
						</Box>
						<Box sx={{
							width: '100%', mt: '20px', bgcolor: L.surface, border: `1px solid ${L.border}`,
							borderRadius: '15px', p: '14px', textAlign: 'left', flex: 'none',
							display: 'flex', flexDirection: 'column', gap: '10px',
						}}>
							<ReceiptLines ck={ck} />
							<SummaryRows ck={ck} dense />
						</Box>
					</Box>
					<Box sx={{
						flex: 'none', display: 'flex', flexDirection: 'column', gap: '10px',
						p: '12px 16px calc(env(safe-area-inset-bottom, 0px) + 20px)',
						borderTop: `1px solid ${L.border}`, bgcolor: L.bg,
					}}>
						<MbPrimaryButton onClick={ck.onDone} sx={{ width: '100%', flex: '1 1 100%', height: 54 }}>
							Back to appointment
						</MbPrimaryButton>
					</Box>
				</>
			) : (
				/* ── checkout form ── */
				<>
					<Box sx={{
						flex: 1, minHeight: 0, overflowY: 'auto', p: '6px 16px 24px',
						display: 'flex', flexDirection: 'column', gap: '20px',
					}}>
						{ck.errorMessage && (
							<Box sx={{
								p: '11px 13px', bgcolor: '#FFF0F0', border: '1px solid #FECACA',
								borderRadius: '12px', color: '#8B1A1A', fontSize: '13px', fontWeight: 600,
							}}>
								{ck.errorMessage}
							</Box>
						)}
						{ck.terminalStatus === 'waiting' && (
							<Box sx={{
								display: 'flex', alignItems: 'center', gap: '10px', p: '12px 13px',
								bgcolor: L.primarySoft, border: `1px solid ${L.border}`, borderRadius: '12px',
								color: L.primaryDeep, fontSize: '13px', fontWeight: 650,
							}}>
								<CircularProgress size={16} sx={{ color: L.primary, flex: 'none' }} />
								<span>Waiting for customer to pay on the Square Terminal…</span>
							</Box>
						)}
						{ck.terminalStatus === 'saving' && (
							<Box sx={{
								display: 'flex', alignItems: 'center', gap: '10px', p: '12px 13px',
								bgcolor: OK_BG, border: `1px solid ${OK_BORDER}`, borderRadius: '12px',
								color: '#25714A', fontSize: '13px', fontWeight: 650,
							}}>
								<CircularProgress size={16} sx={{ color: OK_IC, flex: 'none' }} />
								<span>Payment confirmed! Saving record…</span>
							</Box>
						)}

						{/* Service */}
						<Box component="section">
							<LavSectionTitle>{ck.serviceLines.length > 1 ? 'Services' : 'Service'}</LavSectionTitle>
							{ck.loading ? (
								<Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
									<CircularProgress size={20} sx={{ color: L.primary }} />
								</Box>
							) : (
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
									{ck.serviceLines.map(l => (
										<Box key={l.key} sx={{
											bgcolor: L.field, border: `1px solid ${L.border}`, borderRadius: '15px',
											p: '13px', display: 'flex', alignItems: 'center', gap: '11px',
										}}>
											<Box sx={{
												width: 38, height: 38, flex: 'none', borderRadius: '50%', bgcolor: L.primarySoft,
												color: L.primaryDeep, display: 'flex', alignItems: 'center', justifyContent: 'center',
											}}>
												<Icon name="paw" size={17} />
											</Box>
											<Box sx={{ flex: 1, minWidth: 0 }}>
												<Box component="strong" sx={{ display: 'block', fontSize: '15.5px', fontWeight: 750 }}>
													{l.pet || l.name}
												</Box>
												{l.pet && (
													<Box component="span" sx={{
														display: 'block', fontSize: '12.5px', fontWeight: 600, color: L.muted, mt: '1px',
														whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
													}}>
														{l.name}
													</Box>
												)}
											</Box>
											<Box component="span" sx={{ flex: 'none', fontSize: '16px', fontWeight: 750, fontVariantNumeric: 'tabular-nums' }}>
												{ck.formatCurrency(l.price)}
											</Box>
										</Box>
									))}
								</Box>
							)}
						</Box>

						{/* Add-ons */}
						<Box component="section">
							<LavSectionTitle hint="optional">Add-ons</LavSectionTitle>
							<Box
								component="button"
								type="button"
								aria-haspopup="dialog"
								disabled={ck.isFormLocked}
								onClick={() => setAddonSheet(true)}
								sx={{
									display: 'flex', alignItems: 'center', gap: '9px', width: '100%', height: 48,
									border: `1.5px solid ${L.border}`, borderRadius: '13px', bgcolor: L.field,
									px: '14px', fontSize: '15px', color: L.muted, cursor: 'pointer', fontFamily: 'inherit',
									'&:active': { borderColor: L.primary, bgcolor: L.primarySoft },
									'&:disabled': { opacity: 0.55, cursor: 'default' },
								}}
							>
								<MbIc name="search" size={15} />
								<Box component="span" sx={{ flex: 1, minWidth: 0, textAlign: 'left', fontWeight: 550 }}>
									Search add-ons…
								</Box>
								<Box sx={{
									width: 24, height: 24, flex: 'none', borderRadius: '50%', bgcolor: L.primarySoft,
									color: L.primaryDeep, display: 'flex', alignItems: 'center', justifyContent: 'center',
								}}>
									<MbIc name="plus" size={13} sw={2.2} />
								</Box>
							</Box>
							{(pickedAddons.length > 0 || legacyAddons.length > 0) && (
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', mt: '10px' }}>
									{/* Booked add-ons that no longer match the catalog — kept at
									    their booked price, removable but not incrementable. */}
									{legacyAddons.map(l => (
										<Box key={l.key} sx={{
											bgcolor: L.primarySoft, border: `1.5px solid ${L.primary}`,
											borderRadius: '13px', p: '11px 13px', flex: 'none',
											display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px',
										}}>
											<Box sx={{ minWidth: 0 }}>
												<Box component="span" sx={{ display: 'block', fontWeight: 600, fontSize: '14.5px', color: L.text }}>
													{l.name}
												</Box>
												<Box component="span" sx={{ display: 'block', fontSize: '12.5px', color: L.muted, mt: '2px' }}>
													{ck.formatCurrency(l.price)} · from booking
												</Box>
											</Box>
											<Box
												component="button"
												type="button"
												aria-label={`Remove ${l.name}`}
												onClick={() => ck.onRemoveLegacy && ck.onRemoveLegacy(l.key)}
												disabled={ck.isFormLocked}
												sx={{
													width: 30, height: 30, flex: 'none', borderRadius: '50%',
													border: `1.5px solid ${L.border}`, bgcolor: L.field, color: L.muted,
													display: 'flex', alignItems: 'center', justifyContent: 'center',
													cursor: 'pointer', fontFamily: 'inherit',
													'&:disabled': { opacity: 0.35, cursor: 'default' },
												}}
											>
												<MbIc name="x" size={13} sw={2} />
											</Box>
										</Box>
									))}
									{pickedAddons.map(a => (
										<AddonRow
											key={a.id}
											addon={a}
											count={ck.addOnCounts[a.id]}
											onBump={(d) => bump(a.id, d)}
											formatCurrency={ck.formatCurrency}
											disabled={ck.isFormLocked}
										/>
									))}
									<Box sx={{
										display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
										fontSize: '14px', color: L.muted, pt: '12px', px: '2px',
										borderTop: `1.5px dashed ${L.border}`, mt: '4px',
									}}>
										<span>Add-ons total</span>
										<Box component="strong" sx={{ color: L.text, fontSize: '15px' }}>
											{ck.formatCurrency(ck.addOnsTotal)}
										</Box>
									</Box>
								</Box>
							)}
						</Box>

						{/* Discount */}
						<Box component="section">
							<LavSectionTitle hint="optional">Discount</LavSectionTitle>
							<Box sx={{ display: 'flex', gap: '9px', flexWrap: 'wrap' }}>
								{['10', '15', '25'].map(pct => (
									<TipChip
										key={pct}
										top={`${pct}% off`}
										bottom={`−${ck.formatCurrency(Math.round(ck.subtotal * Number(pct) / 100))}`}
										on={ck.selectedDiscountOption === pct}
										disabled={ck.isFormLocked}
										onClick={() => ck.setSelectedDiscountOption(ck.selectedDiscountOption === pct ? null : pct)}
									/>
								))}
								<TipChip
									top="Custom"
									bottom={ck.selectedDiscountOption === 'custom' && ck.discountAmountCents > 0
										? `−${ck.formatCurrency(ck.discountAmountCents)}`
										: `% or ${ck.cs}`}
									on={ck.selectedDiscountOption === 'custom'}
									disabled={ck.isFormLocked}
									onClick={() => ck.setSelectedDiscountOption(ck.selectedDiscountOption === 'custom' ? null : 'custom')}
								/>
							</Box>
							{ck.selectedDiscountOption === 'custom' && (
								<Box sx={{ mt: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
									{/* percent / fixed unit toggle */}
									<Box role="group" aria-label="Percent or dollar discount" sx={{
										display: 'inline-flex', gap: '2px', height: 46, alignItems: 'center',
										bgcolor: L.surface, border: `1.5px solid ${L.border}`,
										borderRadius: '12px', px: '3px', flex: 'none',
									}}>
										{[['percent', '%'], ['fixed', ck.cs]].map(([unit, symbol]) => (
											<Box
												key={unit}
												component="button"
												type="button"
												onClick={() => ck.setDiscountUnit(unit)}
												disabled={ck.isFormLocked}
												sx={{
													border: 'none', width: 36, height: 36, borderRadius: '9px',
													fontFamily: 'inherit', fontSize: '14px', fontWeight: 750, cursor: 'pointer',
													display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
													bgcolor: ck.discountUnit === unit ? '#FFFFFF' : 'transparent',
													color: ck.discountUnit === unit ? L.primaryDeep : L.muted,
													boxShadow: ck.discountUnit === unit ? '0 1px 2px rgba(46,42,64,0.16)' : 'none',
													'&:disabled': { opacity: 0.5, cursor: 'default' },
												}}
											>
												{symbol}
											</Box>
										))}
									</Box>
									<Box sx={{
										width: 110, display: 'flex', alignItems: 'center', gap: '5px', height: 46,
										border: `1.5px solid ${L.border}`, borderRadius: '12px', bgcolor: L.field,
										px: '13px', flex: 'none',
										'&:focus-within': { borderColor: L.primary, boxShadow: `0 0 0 3px ${L.primarySoft}` },
									}}>
										{ck.discountUnit === 'fixed' && (
											<Box component="span" sx={{ fontSize: '14px', fontWeight: 700, color: L.muted, flex: 'none' }}>{ck.cs}</Box>
										)}
										<Box
											component="input"
											type="text"
											inputMode="decimal"
											placeholder="0"
											value={ck.customDiscountValue}
											onChange={(e) => ck.setCustomDiscountValue(e.target.value.replace(/[^\d.]/g, ''))}
											disabled={ck.isFormLocked}
											sx={{
												width: '100%', minWidth: 0, border: 'none', outline: 'none',
												bgcolor: 'transparent', fontFamily: 'inherit', fontSize: '15px',
												fontWeight: 650, color: L.text,
												'&::placeholder': { color: L.muted, fontWeight: 500 },
											}}
										/>
										{ck.discountUnit === 'percent' && (
											<Box component="span" sx={{ fontSize: '14px', fontWeight: 700, color: L.muted, flex: 'none' }}>%</Box>
										)}
									</Box>
									<Box sx={{ display: 'flex', alignItems: 'baseline', gap: '5px', minWidth: 0, whiteSpace: 'nowrap' }}>
										{ck.discountAmountCents > 0 && (
											<Box component="span" sx={{
												fontSize: '15px', fontWeight: 750, color: '#2F8A5B',
												fontVariantNumeric: 'tabular-nums',
											}}>
												−{ck.formatCurrency(ck.discountAmountCents)}
											</Box>
										)}
										<Box component="span" sx={{ fontSize: '13px', fontWeight: 600, color: L.muted }}>
											off subtotal
										</Box>
									</Box>
								</Box>
							)}
						</Box>

						{/* Payment method */}
						<Box component="section">
							<LavSectionTitle>Payment method</LavSectionTitle>
							<Box
								component="button"
								type="button"
								aria-haspopup="listbox"
								disabled={ck.isFormLocked}
								onClick={() => setPaySheet(true)}
								sx={{
									display: 'flex', alignItems: 'center', gap: '10px', width: '100%', height: 52,
									border: `1.5px solid ${L.border}`, borderRadius: '13px', bgcolor: L.field,
									px: '14px', fontSize: '15px', fontWeight: 650, color: L.text, cursor: 'pointer', fontFamily: 'inherit',
									'& > svg': { color: L.muted },
									'&:active': { borderColor: L.primary, bgcolor: L.primarySoft },
									'&:disabled': { opacity: 0.55, cursor: 'default' },
								}}
							>
								<MbIc name={payMethodIcon(payMethod.value)} size={16} />
								<Box component="span" sx={{ flex: 1, textAlign: 'left' }}>{payMethod.label}</Box>
								<MbIc name="chevron" size={15} />
							</Box>
						</Box>

						{/* Tip */}
						<Box component="section">
							<LavSectionTitle hint="optional">Tip</LavSectionTitle>
							<Box sx={{ display: 'flex', gap: '9px', flexWrap: 'wrap' }}>
								{['15', '18', '25'].map(pct => (
									<TipChip
										key={pct}
										top={`${pct}%`}
										bottom={ck.formatCurrency(Math.round(ck.discountedSubtotal * Number(pct) / 100))}
										on={ck.selectedTipOption === pct}
										disabled={ck.isFormLocked}
										onClick={() => ck.setSelectedTipOption(ck.selectedTipOption === pct ? null : pct)}
									/>
								))}
								<TipChip
									top="Custom"
									bottom={ck.selectedTipOption === 'custom' && ck.tipsAmountCents > 0
										? ck.formatCurrency(ck.tipsAmountCents)
										: `% or ${ck.cs}`}
									on={ck.selectedTipOption === 'custom'}
									disabled={ck.isFormLocked}
									onClick={() => ck.setSelectedTipOption(ck.selectedTipOption === 'custom' ? null : 'custom')}
								/>
							</Box>
							{ck.selectedTipOption === 'custom' && (
								<Box sx={{ mt: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
									{/* percent / fixed unit toggle */}
									<Box role="group" aria-label="Percent or dollar tip" sx={{
										display: 'inline-flex', gap: '2px', height: 46, alignItems: 'center',
										bgcolor: L.surface, border: `1.5px solid ${L.border}`,
										borderRadius: '12px', px: '3px', flex: 'none',
									}}>
										{[['percent', '%'], ['fixed', ck.cs]].map(([unit, symbol]) => (
											<Box
												key={unit}
												component="button"
												type="button"
												onClick={() => ck.setTipUnit(unit)}
												disabled={ck.isFormLocked}
												sx={{
													border: 'none', width: 36, height: 36, borderRadius: '9px',
													fontFamily: 'inherit', fontSize: '14px', fontWeight: 750, cursor: 'pointer',
													display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
													bgcolor: ck.tipUnit === unit ? '#FFFFFF' : 'transparent',
													color: ck.tipUnit === unit ? L.primaryDeep : L.muted,
													boxShadow: ck.tipUnit === unit ? '0 1px 2px rgba(46,42,64,0.16)' : 'none',
													'&:disabled': { opacity: 0.5, cursor: 'default' },
												}}
											>
												{symbol}
											</Box>
										))}
									</Box>
									<Box sx={{
										width: 110, display: 'flex', alignItems: 'center', gap: '5px', height: 46,
										border: `1.5px solid ${L.border}`, borderRadius: '12px', bgcolor: L.field,
										px: '13px', flex: 'none',
										'&:focus-within': { borderColor: L.primary, boxShadow: `0 0 0 3px ${L.primarySoft}` },
									}}>
										{ck.tipUnit === 'fixed' && (
											<Box component="span" sx={{ fontSize: '14px', fontWeight: 700, color: L.muted, flex: 'none' }}>{ck.cs}</Box>
										)}
										<Box
											component="input"
											type="text"
											inputMode="decimal"
											placeholder="0"
											value={ck.customTipAmount}
											onChange={(e) => ck.setCustomTipAmount(e.target.value.replace(/[^\d.]/g, ''))}
											disabled={ck.isFormLocked}
											sx={{
												width: '100%', minWidth: 0, border: 'none', outline: 'none',
												bgcolor: 'transparent', fontFamily: 'inherit', fontSize: '15px',
												fontWeight: 650, color: L.text,
												'&::placeholder': { color: L.muted, fontWeight: 500 },
											}}
										/>
										{ck.tipUnit === 'percent' && (
											<Box component="span" sx={{ fontSize: '14px', fontWeight: 700, color: L.muted, flex: 'none' }}>%</Box>
										)}
									</Box>
									<Box sx={{ display: 'flex', alignItems: 'baseline', gap: '5px', minWidth: 0, whiteSpace: 'nowrap' }}>
										{ck.tipsAmountCents > 0 && (
											<Box component="span" sx={{
												fontSize: '15px', fontWeight: 750, color: L.primaryDeep,
												fontVariantNumeric: 'tabular-nums',
											}}>
												+{ck.formatCurrency(ck.tipsAmountCents)}
											</Box>
										)}
										<Box component="span" sx={{ fontSize: '13px', fontWeight: 600, color: L.muted }}>
											added as tip
										</Box>
									</Box>
								</Box>
							)}
						</Box>

						{/* Notes */}
						<Box component="section">
							<Box
								component="textarea"
								rows={2}
								placeholder="Add any notes about this payment…"
								value={ck.notes}
								onChange={(e) => ck.setNotes(e.target.value)}
								disabled={ck.isFormLocked}
								sx={{
									width: '100%', border: `1.5px solid ${L.border}`, borderRadius: '13px',
									p: '12px 14px', fontFamily: 'inherit', fontSize: '15px', resize: 'vertical',
									bgcolor: L.field, minHeight: 72, color: L.text, outline: 'none', boxSizing: 'border-box',
									'&::placeholder': { color: L.muted },
									'&:focus': { borderColor: L.primary, boxShadow: `0 0 0 3px ${L.primarySoft}` },
									'&:disabled': { opacity: 0.55 },
								}}
							/>
						</Box>
					</Box>

					{/* ── sticky footer: total bar + CTA ── */}
					<Box sx={{
						flex: 'none', display: 'flex', flexDirection: 'column', gap: '10px',
						p: '12px 16px calc(env(safe-area-inset-bottom, 0px) + 20px)',
						borderTop: `1px solid ${L.border}`, bgcolor: L.bg,
					}}>
						{ck.terminalStatus ? (
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
								<Box
									component="button"
									type="button"
									onClick={ck.onCancelTerminal}
									disabled={ck.terminalStatus === 'saving'}
									sx={{
										flex: 'none', height: 52, px: '18px', borderRadius: '14px',
										border: `1.5px solid ${L.border}`, bgcolor: '#FFFFFF', color: L.muted,
										fontSize: '15px', fontWeight: 650, fontFamily: 'inherit', cursor: 'pointer',
										'&:disabled': { opacity: 0.5, cursor: 'default' },
									}}
								>
									Cancel
								</Box>
								<Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '9px' }}>
									<CircularProgress size={17} sx={{ color: L.primary }} />
									<Box component="span" sx={{ fontSize: '13.5px', fontWeight: 650, color: L.muted }}>
										{ck.terminalStatus === 'saving' ? 'Saving payment…' : 'Waiting for payment…'}
									</Box>
								</Box>
							</Box>
						) : (
							<>
								<Box
									component="button"
									type="button"
									aria-expanded={receiptSheet}
									onClick={() => setReceiptSheet(true)}
									sx={{
										display: 'flex', alignItems: 'center', gap: '8px', width: '100%', minHeight: 46,
										border: `1.5px solid ${L.border}`, borderRadius: '13px', bgcolor: L.surface,
										p: '8px 14px', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit',
										'&:active': { borderColor: L.primary },
									}}
								>
									<Box component="span" sx={{ color: L.muted, fontWeight: 650 }}>
										Total{' '}
										<Box component="em" sx={{ fontStyle: 'normal', fontWeight: 600, fontSize: '12px' }}>
											· incl. tax{ck.tipsAmountCents > 0 ? ' & tip' : ''}
										</Box>
									</Box>
									<Box component="strong" sx={{ ml: 'auto', fontSize: '16.5px', fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: L.text }}>
										{ck.formatCurrency(ck.totalAmount)}
									</Box>
									<Box component="span" sx={{ display: 'flex', color: L.muted }}>
										<MbIc name="chevup" size={15} sw={2} />
									</Box>
								</Box>
								<MbPrimaryButton
									onClick={handleComplete}
									disabled={ck.saving || ck.loading || ck.totalAmount <= 0}
									sx={{ width: '100%', flex: '1 1 100%', height: 54 }}
								>
									{ck.saving
										? <CircularProgress size={17} sx={{ color: '#FFFFFF' }} />
										: <MbIc name="card" size={16} />}
									<span>{ck.saving ? 'Processing…' : completeLabel}</span>
								</MbPrimaryButton>
							</>
						)}
					</Box>
				</>
			)}

			{/* ── add-ons sheet ── */}
			<MobileSheet open={addonSheet && !ck.paidInfo} onClose={() => setAddonSheet(false)} title="Add-ons">
				<Box sx={{
					flex: 'none', display: 'flex', alignItems: 'center', gap: '9px', mb: '10px',
					border: `1.5px solid ${L.border}`, borderRadius: '13px', px: '14px', height: 48,
					bgcolor: L.field, color: L.muted,
					'&:focus-within': { borderColor: L.primary, boxShadow: `0 0 0 3px ${L.primarySoft}` },
				}}>
					<MbIc name="search" size={15} />
					<Box
						component="input"
						type="text"
						placeholder="Search add-ons…"
						value={ck.addOnSearch}
						onChange={(e) => ck.setAddOnSearch(e.target.value)}
						sx={{
							border: 'none', outline: 'none', flex: 1, minWidth: 0, font: 'inherit',
							fontSize: '15px', bgcolor: 'transparent', color: L.text,
							'&::placeholder': { color: L.muted },
						}}
					/>
					{ck.addOnSearch && (
						<Box
							component="button"
							type="button"
							aria-label="Clear search"
							onClick={() => ck.setAddOnSearch('')}
							sx={{
								ml: '4px', width: 26, height: 26, flex: 'none', border: 'none', borderRadius: '50%',
								bgcolor: L.surface, color: L.muted, display: 'flex', alignItems: 'center',
								justifyContent: 'center', p: 0, cursor: 'pointer',
							}}
						>
							<MbIc name="x" size={13} />
						</Box>
					)}
				</Box>
				<Box sx={{ overflowY: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column', gap: '8px', pr: '3px' }}>
					{filteredAddons.map(a => (
						<AddonRow
							key={a.id}
							addon={a}
							count={ck.addOnCounts[a.id] || 0}
							onBump={(d) => bump(a.id, d)}
							formatCurrency={ck.formatCurrency}
							disabled={ck.isFormLocked}
						/>
					))}
					{filteredAddons.length === 0 && (
						<Box sx={{ p: '24px 10px', textAlign: 'center', color: L.muted, fontSize: '13.5px' }}>
							No add-ons match “{ck.addOnSearch}”
						</Box>
					)}
				</Box>
				<Box sx={{
					flex: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
					fontSize: '14px', color: L.muted, pt: '12px', mt: '12px',
					borderTop: `1.5px dashed ${L.border}`, px: '2px',
				}}>
					<span>Add-ons total</span>
					<Box component="strong" sx={{ color: L.text, fontSize: '15px' }}>
						{ck.formatCurrency(ck.addOnsTotal)}
					</Box>
				</Box>
				<SheetCancelButton onClick={() => setAddonSheet(false)}>Done</SheetCancelButton>
			</MobileSheet>

			{/* ── payment method sheet ── */}
			<MobileSheet open={paySheet && !ck.paidInfo} onClose={() => setPaySheet(false)} title="Payment method">
				<Box role="listbox" sx={{ overflowY: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
					{ck.availablePaymentMethods.map(m => {
						const on = ck.paymentMethod === m.value
						return (
							<Box
								component="button"
								type="button"
								role="option"
								aria-selected={on}
								key={m.value}
								onClick={() => { ck.setPaymentMethod(m.value); setPaySheet(false) }}
								sx={{
									display: 'flex', alignItems: 'center', gap: '12px', width: '100%', minHeight: 54,
									flex: 'none', border: `1.5px solid ${on ? L.primary : L.border}`,
									borderRadius: '14px', bgcolor: on ? L.primarySoft : L.field,
									p: '8px 13px', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
									'&:active': { bgcolor: on ? L.primarySoft : L.surface },
								}}
							>
								<Box sx={{
									width: 36, height: 36, flex: 'none', borderRadius: '11px',
									bgcolor: on ? '#FFFFFF' : L.surface, color: on ? L.primary : L.muted,
									display: 'flex', alignItems: 'center', justifyContent: 'center',
								}}>
									<MbIc name={payMethodIcon(m.value)} size={17} />
								</Box>
								<Box component="span" sx={{
									flex: 1, minWidth: 0, fontSize: '15px', fontWeight: 700,
									color: on ? L.primaryDeep : L.text,
								}}>
									{m.label}
								</Box>
								{on && (
									<Box sx={{ display: 'flex', flex: 'none', color: L.primaryDeep }}>
										<MbIc name="tick" size={16} sw={2.2} />
									</Box>
								)}
							</Box>
						)
					})}
				</Box>
			</MobileSheet>

			{/* ── receipt sheet ── */}
			<MobileSheet open={receiptSheet && !ck.paidInfo} onClose={() => setReceiptSheet(false)} title="Receipt">
				<Box sx={{
					overflowY: 'auto', minHeight: 0, p: '2px 4px 14px',
					display: 'flex', flexDirection: 'column', gap: '10px',
				}}>
					<ReceiptLines ck={ck} />
					<SummaryRows ck={ck} />
				</Box>
				<MbPrimaryButton
					onClick={handleComplete}
					disabled={ck.saving || ck.totalAmount <= 0}
					sx={{ width: '100%', flex: 'none', height: 54 }}
				>
					{ck.saving
						? <CircularProgress size={17} sx={{ color: '#FFFFFF' }} />
						: <MbIc name="card" size={16} />}
					<span>{ck.saving ? 'Processing…' : completeLabel}</span>
				</MbPrimaryButton>
				<SheetCancelButton onClick={() => setReceiptSheet(false)}>Keep editing</SheetCancelButton>
			</MobileSheet>
		</Box>
	)
}
